"use client";

import React, { memo, useState, useEffect } from "react";
import { TokenTableRow } from "./TokenTableRow";
import { TokenTableRowSkeleton } from "./TokenTableRowSkeleton";
import { MobileSortDropdown } from "./elements/MobileSortDropdown";
import { ColumnHeaderButton } from "./elements/ColumnHeaderButton";

import styles from "./TokensTable.module.scss";
import tableColStyles from "./TableColumns.module.scss";
import type { Token } from '@/utils/types/token';

// Remove the local TokenTableToken interface and use the shared Token type

const PAGE_SIZE = 25;
const SSR_TOTAL_PAGES = 8;
// const MAX_TOKENS = 1000;
// const SORT_KEY = "sol_web_maintable_setting";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const columns = [
  { key: "token", label: "Token", sortable: true },
  { key: "price", label: "Price", sortable: true },
  { key: "marketCap", label: "Market Cap", sortable: true },
  { key: "liquidity", label: "Liquidity", sortable: true },
  { key: "volume24h", label: "Volume 24h", sortable: true },
  { key: "buy", label: "", sortable: false },
];

const sortKeyToApi: Record<string, string> = {
  price: "usdPrice",
  marketCap: "mc",
  liquidity: "liquidity",
  volume24h: "volume24h",
};

// function getSortSetting() {
//   if (typeof window === "undefined") return { key: "marketCap", dir: "desc" };
//   try {
//     const raw = localStorage.getItem(SORT_KEY);
//     if (raw) return JSON.parse(raw);
//   } catch { }
//   return { key: "marketCap", dir: "desc" };
// }

// function setSortSetting(setting: { key: string; dir: string }) {
//   if (typeof window !== "undefined") {
//     localStorage.setItem(SORT_KEY, JSON.stringify(setting));
//   }
// }

interface TokenTableProps {
  tokens: Token[];
  page?: number;
  isStocks?: boolean;
}

export const TokenTable: React.FC<TokenTableProps> = memo(function TokenTable({ tokens: initialTokens, page: initialPage, isStocks = false }) {
  const [page, setPage] = useState(initialPage && initialPage > 0 ? initialPage : 1);
  const [sort, setSort] = useState<{ key: string; dir: string }>({ key: "marketCap", dir: "desc" });
  const [tokens, setTokens] = useState<Token[]>(initialTokens);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Mobile sort/column selector state
  const [mobileSortDropdownOpen, setMobileSortDropdownOpen] = useState(false);
  const [mobileVisibleColumn, setMobileVisibleColumn] = useState('marketCap');

  // Mobile columns config
  const mobileColumns = [
    { key: 'marketCap', label: 'Market cap' },
    { key: 'volume24h', label: 'Volume 24h' },
    { key: 'liquidity', label: 'Liquidity' },
    { key: 'price', label: 'Price' },
  ];

  // Hide one column on mobile if needed
  // const getMobileColumns = () => {
  //   // Always show token and buy columns, show only one of the others
  //   return ['token', mobileVisibleColumn, 'buy'];
  // };

  // In mobile, add forceShow to selected column, forceHide to others
  const getColumnClass = (colKey: string) => {
    if (windowWidth >= 768) return '';
    if (colKey === mobileVisibleColumn || colKey === 'token' || colKey === 'buy') {
      return tableColStyles.forceShow;
    }
    return tableColStyles.forceHide;
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to determine if a column should be visible on mobile
  const isMobileColumnVisible = (colKey: string) => {
    if (windowWidth >= 768) return true;
    // Only show the selected column (plus token and buy) on mobile
    return colKey === 'token' || colKey === 'buy' || colKey === mobileVisibleColumn;
  };

  // When changing sort or column on mobile, always fetch new tokens and update both sort and mobileVisibleColumn
  const handleMobileSort = async (colKey: string) => {
    let dir = 'desc';
    if (sort.key === colKey) {
      dir = sort.dir === 'desc' ? 'asc' : 'desc';
    }
    setSort({ key: colKey, dir });
    setMobileVisibleColumn(colKey);
    setMobileSortDropdownOpen(false);
    setLoading(true);
    try {
      const apiSortKey = sortKeyToApi[colKey as keyof typeof sortKeyToApi] || 'mc';
      const res = await fetch(`/tkn_lnd_pg_api/tokens/?sortParam=${apiSortKey}&sortDir=${dir}&take=200`);
      const data = await res.json();
      if (Array.isArray(data.tokens)) {
        setTokens(data.tokens);
        const maxPage = Math.ceil(data.tokens.length / PAGE_SIZE);
        setPage((prev) => (prev > maxPage ? 1 : prev));
      }
    } finally {
      setLoading(false);
    }
  };

  // const fetchTokens = async (apiSortKey: string, dir: 'asc' | 'desc') => {
  //   setLoading(true);
  //   try {
  //     const res = await fetch(`/tkn_lnd_pg_api/tokens/?sortParam=${apiSortKey}&sortDir=${dir}&take=200`);
  //     if (!res.ok) throw new Error('Failed to fetch');
  //     const data = await res.json();
  //     const tokens = Array.isArray(data) ? data : data?.tokens || [];
  //     setTokens(tokens);
  //     setSort({ key: apiSortKey, dir });
  //   } catch (error) {
  //     console.error('Error fetching tokens:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleSort = async (key: string) => {
    if (loading) return;
    
    const apiSortKey = sortKeyToApi[key] || key;
    const dir = sort.key === key && sort.dir === 'desc' ? 'asc' : 'desc';
    
    try {
      const res = await fetch(`/tkn_lnd_pg_api/tokens/?sortParam=${apiSortKey}&sortDir=${dir}&take=200&onlyStock=${isStocks}`);
      if (!res.ok) throw new Error('Failed to fetch');
      const data = await res.json();
      const tokens = Array.isArray(data) ? data : data?.tokens || [];
      setTokens(tokens);
      setSort({ key: key, dir });
    } catch (error) {
      console.error('Error fetching tokens:', error);
    }
  };

  // Sync page state with prop if it changes and is valid
  useEffect(() => {
    if (typeof initialPage === 'number' && initialPage > 0 && initialPage !== page) {
      setPage(initialPage);
      // Optionally scroll to table top for UX
      if (typeof window !== 'undefined') {
        const table = document.getElementById('token-table');
        if (table) {
          // Use a fixed nav height (88px) for scroll offset
          const navHeight = 88;
          const rect = table.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - navHeight;
          window.scrollTo({ top: targetY, behavior: 'smooth' });
        }
      }
    }
  }, [initialPage]);

  useEffect(() => {
    setTokens(initialTokens);
  }, [initialTokens]);

  // Remove local sorting, just use tokens as returned from API

  const pagedTokens = tokens.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className={`${styles.tableWrapper} scroll-mt-25`} id="token-table" style={{ scrollMarginTop: '80px' }}>
      {/* Mobile sort/column selector row */}
      <MobileSortDropdown
        open={mobileSortDropdownOpen}
        sort={sort}
        mobileColumns={mobileColumns}
        onSelect={handleMobileSort}
        onToggle={() => setMobileSortDropdownOpen((v) => !v)}
      />
      <div className={styles.tokenTable}>
        <div className={styles.header}>
          <div className={styles.tokenLink}>
            <div className={`${tableColStyles.tableCellHeader} ${tableColStyles.tableCol} ${tableColStyles.tableColToken}`} >
              {isStocks ? 'Stocks' : 'Tokens'}
            </div>
            <ColumnHeaderButton
              colKey="price"
              label="Price"
              active={sort.key === 'price'}
              sortDir={sort.dir === 'desc' ? 'desc' : 'asc'}
              onSort={handleSort}
              getColumnClass={getColumnClass}
              isVisible={isMobileColumnVisible('price')}
              colClass={tableColStyles.tableColPrice}
            />
            <ColumnHeaderButton
              colKey="marketCap"
              label="M.Cap"
              active={sort.key === 'marketCap'}
              sortDir={sort.dir === 'desc' ? 'desc' : 'asc'}
              onSort={handleSort}
              getColumnClass={getColumnClass}
              isVisible={isMobileColumnVisible('marketCap')}
              colClass={tableColStyles.tableColMarketCap}
            />
            <ColumnHeaderButton
              colKey="liquidity"
              label="Liq."
              active={sort.key === 'liquidity'}
              sortDir={sort.dir === 'desc' ? 'desc' : 'asc'}
              onSort={handleSort}
              getColumnClass={getColumnClass}
              isVisible={isMobileColumnVisible('liquidity')}
              colClass={tableColStyles.tableColLiquidity}
            />
            <ColumnHeaderButton
              colKey="volume24h"
              label="Volume 24h"
              active={sort.key === 'volume24h'}
              sortDir={sort.dir === 'desc' ? 'desc' : 'asc'}
              onSort={handleSort}
              getColumnClass={getColumnClass}
              isVisible={isMobileColumnVisible('volume24h')}
              colClass={tableColStyles.tableColVolume}
            />
          </div>
          <div className={`${tableColStyles.tableCellHeader} ${tableColStyles.tableCol} ${tableColStyles.tableColBuy}`} >
            {/* Empty for buy button */}
          </div>
        </div>
        <div className={styles.body}>
          {loading || tokens.length === 0
            ? Array.from({ length: 25 }).map((_, i) => <TokenTableRowSkeleton key={i} />)
            : pagedTokens.map((token, index) => (
              <TokenTableRow 
                key={token.mint} 
                token={token} 
                windowWidth={windowWidth} 
                mobileVisibleColumn={mobileVisibleColumn}
                index={index}
                isStocks={isStocks}
              />
            ))}
        </div>
      </div>
      {/* Hide pagination if isStocks is true */}
      {!isStocks && (
        <div className={styles.pagination}>
          {Array.from({ length: SSR_TOTAL_PAGES }, (_, i) => {
            const isActive = page === i + 1;
            let pageHref = '/prices/';
            if (i > 0) pageHref = `/prices/?page=${i + 1}`;
            return (
              <a
                key={i + 1}
                className={isActive ? `${styles.active}` : ''}
                href={pageHref}
                onClick={typeof window !== 'undefined' ? (e) => {
                  e.preventDefault();
                  setPage(i + 1);
                  window.history.pushState({}, '', pageHref);
                  const table = document.getElementById('token-table');
                  if (table) {
                    const navHeight = 88;
                    const rect = table.getBoundingClientRect();
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const targetY = rect.top + scrollTop - navHeight;
                    window.scrollTo({ top: targetY, behavior: 'smooth' });
                  }
                } : undefined}
              >
                {i + 1}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
});
